/**
 * useSiteNav — storefront reader for `/site_settings/nav` with 60s cache.
 *
 * EPIC-13 · FEAT-13.4 · STORY-13.4.05.
 *
 * Mirrors the cache pattern used by `useSystemPageStrings` /
 * `useCatalogPageConfig`: synchronous default render before fetch,
 * de-duped concurrent fetches via shared promise, manual invalidation
 * so admin writes can bust the cache on save.
 *
 * Returns `{ nav, source, loading, error }`. `nav` is ALWAYS a
 * fully-shaped DEFAULT_NAV-or-merged object (header[], 4 footer cols,
 * announcement) so storefront callers can render unconditionally.
 *
 * Consumers (Header / Footer) typically:
 *   const { nav } = useSiteNav();
 *   nav.header.map((item) => {
 *     const resolved = resolveNavLink(item);
 *     return <a href={hrefOf(resolved)}>{item.label}</a>;
 *   });
 */
import { useEffect, useRef, useState } from 'react';
import { getNav } from 'services/siteSettingsNavService';
import { mergeNav, DEFAULT_NAV } from 'services/siteSettingsNav';
import logger from 'utils/logger';

const CACHE_TTL_MS = 60_000;

const _cache = new Map();

export function invalidateSiteNavCache() {
  _cache.clear();
}

function readCache(ttlMs) {
  const entry = _cache.get('nav');
  if (!entry) return null;
  const fresh = ttlMs > 0 && (Date.now() - entry.fetchedAt) < ttlMs;
  if (!fresh) return null;
  return entry;
}

async function fetchOnce() {
  try {
    const res = await getNav();
    return { nav: res.nav, source: res.source };
  } catch (err) {
    logger.warn('[useSiteNav] fetchOnce failed:', err);
    return { nav: mergeNav(DEFAULT_NAV), source: 'fallback:error' };
  }
}

export function useSiteNav({ ttlMs = CACHE_TTL_MS } = {}) {
  const initial = (() => {
    const cached = readCache(ttlMs);
    if (cached) {
      return { nav: cached.nav, source: cached.source, loading: false, error: null };
    }
    return {
      nav: mergeNav(DEFAULT_NAV),
      source: 'default',
      loading: true,
      error: null,
    };
  })();

  const [state, setState] = useState(initial);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    const cached = readCache(ttlMs);
    if (cached) {
      setState({ nav: cached.nav, source: cached.source, loading: false, error: null });
      return () => { cancelRef.current = true; };
    }

    const entry = _cache.get('nav');
    const inFlight = entry?.promise;
    const promise = inFlight || fetchOnce().then((res) => {
      _cache.set('nav', { fetchedAt: Date.now(), nav: res.nav, source: res.source });
      return res;
    });
    if (!inFlight) _cache.set('nav', { ...(entry || {}), promise });

    setState((prev) => ({ ...prev, loading: true }));
    promise.then((res) => {
      if (cancelRef.current) return;
      setState({ nav: res.nav, source: res.source, loading: false, error: null });
    }).catch((err) => {
      if (cancelRef.current) return;
      setState({
        nav: mergeNav(DEFAULT_NAV),
        source: 'fallback:error',
        loading: false,
        error: err,
      });
    });

    return () => { cancelRef.current = true; };
  }, [ttlMs]);

  return state;
}

export default useSiteNav;
